/**
 * Local persistence (the web equivalent of Unity's PlayerPrefs).
 *
 * Credentials are persisted for login convenience, mirroring Unity PlayerPrefs, but browser storage
 * is never trusted as identity or authorization. The gateway still derives the only web identity from
 * validated Telegram initData and rewrites LoadLogin to "TG:<user_id>". The pseudo-MAC helper below is
 * retained only for legacy development harnesses.
 */
const PSEUDO_MAC_KEY = 'acegame.pseudoMac';
const LOGIN_CREDENTIALS_KEY = 'acegame.loginCredentials.v1';
const TELEGRAM_LOGIN_CREDENTIALS_KEY = 'acegame_login_credentials_v1';
const LAST_DEPOSIT_AMOUNT_KEY = 'acegame.wallet.lastDeposit.v1';
const TELEGRAM_STORAGE_TIMEOUT_MS = 1_500;
/**
 * Pre-rebrand key. Existing devices minted their identity under `pardis.pseudoMac`; we adopt that
 * value verbatim on first run so the device keeps the same pseudo-MAC (and thus the same server-side
 * account binding) across the rename. Left in place — not deleted — so an older client build can still
 * read it after a rollback.
 */
const LEGACY_PSEUDO_MAC_KEY = 'pardis.pseudoMac';

export interface StoredLoginCredentials {
  username: string;
  password: string;
}

type TelegramStorageCallback = (error: string | null, result?: string | boolean | null) => void;
type TelegramKeyValueStorage = {
  setItem(key: string, value: string, callback?: TelegramStorageCallback): unknown;
  getItem(key: string, callback: TelegramStorageCallback): unknown;
  removeItem(key: string, callback?: TelegramStorageCallback): unknown;
};

function telegramCredentialStores(): TelegramKeyValueStorage[] {
  if (typeof window === 'undefined') return [];
  const webApp = window.Telegram?.WebApp as unknown as {
    SecureStorage?: TelegramKeyValueStorage;
    DeviceStorage?: TelegramKeyValueStorage;
  } | undefined;
  return [webApp?.SecureStorage, webApp?.DeviceStorage].filter(
    (store): store is TelegramKeyValueStorage => Boolean(store),
  );
}

function parseLoginCredentials(raw: string | null): StoredLoginCredentials | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredLoginCredentials>;
    if (typeof value.username !== 'string' || !value.username.trim() ||
        typeof value.password !== 'string' || !value.password) {
      return null;
    }
    return { username: value.username.trim(), password: value.password };
  } catch {
    return null;
  }
}

function getTelegramItem(storage: TelegramKeyValueStorage): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: string | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), TELEGRAM_STORAGE_TIMEOUT_MS);
    try {
      storage.getItem(TELEGRAM_LOGIN_CREDENTIALS_KEY, (error, value) => {
        finish(!error && typeof value === 'string' ? value : null);
      });
    } catch {
      finish(null);
    }
  });
}

function setTelegramItem(storage: TelegramKeyValueStorage, value: string): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (stored: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(stored);
    };
    const timer = setTimeout(() => finish(false), TELEGRAM_STORAGE_TIMEOUT_MS);
    try {
      storage.setItem(TELEGRAM_LOGIN_CREDENTIALS_KEY, value, (error, stored) => {
        finish(!error && stored !== false);
      });
    } catch {
      finish(false);
    }
  });
}

function removeTelegramItem(storage: TelegramKeyValueStorage): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, TELEGRAM_STORAGE_TIMEOUT_MS);
    try {
      storage.removeItem(TELEGRAM_LOGIN_CREDENTIALS_KEY, finish);
    } catch {
      finish();
    }
  });
}

/**
 * Scope persisted credentials to the Telegram account when possible. This value is only a storage
 * namespace; the gateway's validated session remains the authority for the actual login identity.
 */
function loginCredentialKey(): string {
  if (typeof window !== 'undefined') {
    const telegramUserId = (window.Telegram?.WebApp as unknown as {
      initDataUnsafe?: { user?: { id?: number } };
    } | undefined)?.initDataUnsafe?.user?.id;
    if (typeof telegramUserId === 'number' && Number.isSafeInteger(telegramUserId)) {
      return `${LOGIN_CREDENTIALS_KEY}:telegram:${telegramUserId}`;
    }
  }

  if (typeof location !== 'undefined') {
    const client = new URLSearchParams(location.search).get('client')?.trim().toLowerCase();
    if (client === 'a' || client === 'b') return `${LOGIN_CREDENTIALS_KEY}:development:${client}`;
  }

  return `${LOGIN_CREDENTIALS_KEY}:browser`;
}

/** Read the last successfully authenticated credentials for this Telegram/development identity. */
export async function loadLoginCredentials(): Promise<StoredLoginCredentials | null> {
  for (const storage of telegramCredentialStores()) {
    const credentials = parseLoginCredentials(await getTelegramItem(storage));
    if (credentials) return credentials;
  }

  try {
    return parseLoginCredentials(localStorage.getItem(loginCredentialKey()));
  } catch {
    return null;
  }
}

/** Persist credentials only after the server has confirmed a successful login. */
export async function saveLoginCredentials(username: string, password: string): Promise<void> {
  const normalizedUsername = username.trim();
  if (!normalizedUsername || !password) return;
  const serialized = JSON.stringify({ username: normalizedUsername, password });

  // Keep a synchronous browser fallback for development and older Telegram clients.
  try {
    localStorage.setItem(loginCredentialKey(), serialized);
  } catch {
    /* persistence is best-effort (private browsing may deny localStorage) */
  }

  for (const storage of telegramCredentialStores()) {
    if (await setTelegramItem(storage, serialized)) return;
  }
}

/** Clear credentials only for the current Telegram/development identity. */
export async function clearLoginCredentials(): Promise<void> {
  try {
    localStorage.removeItem(loginCredentialKey());
  } catch {
    /* persistence is best-effort */
  }
  await Promise.all(telegramCredentialStores().map((storage) => removeTelegramItem(storage)));
}

function lastDepositAmountKey(userId: bigint): string | null {
  return userId > 0n ? `${LAST_DEPOSIT_AMOUNT_KEY}:${userId}` : null;
}

/** Read the last server-accepted deposit for this authenticated game account. */
export function loadLastDepositAmount(userId: bigint): bigint | null {
  const key = lastDepositAmountKey(userId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw || !/^[1-9]\d*$/.test(raw)) return null;
    const amount = BigInt(raw);
    return amount > 0n ? amount : null;
  } catch {
    return null;
  }
}

/** Persist a deposit only after the server has returned a payment-gateway URL. */
export function saveLastDepositAmount(userId: bigint, amount: bigint): void {
  const key = lastDepositAmountKey(userId);
  if (!key || amount <= 0n) return;
  try {
    localStorage.setItem(key, amount.toString());
  } catch {
    /* persistence is best-effort (private browsing may deny localStorage) */
  }
}

/**
 * Dev two-client testing: `?client=a` / `?client=b` forces a stable, distinct pseudo-MAC so two
 * panes/windows on the same machine don't share one identity (same MAC → the server evicts the
 * first session). Returns null when no `client` param is present, so normal users are unaffected.
 */
function clientOverrideMac(): string | null {
  if (typeof location === 'undefined') return null;
  const client = new URLSearchParams(location.search).get('client');
  if (!client) return null;
  const id = client.trim().toLowerCase();
  return /^[a-z0-9_-]{1,16}$/.test(id) ? `WEB:web-${id}` : null;
}

export function getPseudoMac(): string {
  const override = clientOverrideMac();
  if (override) {
    // Persist under a per-client key so a manual reload of that pane keeps the same identity.
    localStorage.setItem(`${PSEUDO_MAC_KEY}.${override}`, override);
    return override;
  }
  let mac = localStorage.getItem(PSEUDO_MAC_KEY);
  if (!mac) {
    // Adopt a pre-rebrand identity if one exists; otherwise mint a fresh one.
    mac = localStorage.getItem(LEGACY_PSEUDO_MAC_KEY) ?? 'WEB:' + crypto.randomUUID();
    localStorage.setItem(PSEUDO_MAC_KEY, mac);
  }
  return mac;
}
